<template>
  <v-container fluid>
    <v-row class="my-0">
      <v-col class="order-1 order-sm-0" cols="12" :sm="viewFilters ? 9 : 12">
        <v-card>
          <v-toolbar dark color="secondary">
            <v-toolbar-title class="font-weight-black">
              {{ $tc('calendar', 2) }} - {{ $tc(`month_list.${currentMonth}`) }}
            </v-toolbar-title>
            <v-spacer />

            <!-- Ir al periodo anterior -->
            <v-btn class="toolbar-btn" text @click="$refs.calendar.prev()">
              <v-icon>fa-arrow-left</v-icon>
            </v-btn>

            <!-- Ir al siguiente periodo -->
            <v-btn class="toolbar-btn" text @click="$refs.calendar.next()">
              <v-icon>fa-arrow-right</v-icon>
            </v-btn>

            <!-- Abrir filtros -->
            <i-btn
              v-if="!viewFilters"
              classes="toolbar-btn"
              class="mx-1"
              icon="fas fa-ellipsis-v"
              text
              :title="$t('filter')"
              @click="openFilters"
            />
          </v-toolbar>

          <v-row class="m-0">
            <v-col cols="12" class="pt-0 pb-4">
              <v-sheet class="calendar-container mt-4">
                <v-row class="mb-2">
                  <!-- Selector para el tipo de evento -->
                  <v-col cols="6" sm="3">
                    <v-select
                      v-model="type"
                      :items="types"
                      item-text="label"
                      item-value="value"
                      dense
                      outlined
                      hide-details
                      class="ma-2"
                      :label="$t('type')"
                    />
                  </v-col>

                  <!-- Switch para filtrar mantenimiento de equipo -->
                  <v-col cols="12" sm="3" class="d-flex align-center">
                    <v-icon
                      small
                      :color="
                        filter_type.includes('maintenance')
                          ? notificationsColor.maintenance || 'orange'
                          : 'grey'
                      "
                      class="mr-2"
                    >
                      fa-wrench
                    </v-icon>
                    <v-switch
                      v-model="filter_type"
                      :label="$tc('equipment_maintenance', true)"
                      :color="notificationsColor.maintenance"
                      value="maintenance"
                      @change="filter"
                      hide-details
                      class="ma-0 pa-0"
                    />
                  </v-col>

                  <!-- Switch para filtrar gastos -->
                  <v-col cols="12" sm="3" class="d-flex align-center">
                    <v-icon
                      small
                      :color="
                        filter_type.includes('expense')
                          ? notificationsColor.expense || 'green'
                          : 'grey'
                      "
                      class="mr-2"
                    >
                      fa-money-bill
                    </v-icon>
                    <v-switch
                      v-model="filter_type"
                      :label="$tc('expense', true)"
                      :color="notificationsColor.expense"
                      value="expense"
                      @change="filter"
                      hide-details
                      class="ma-0 pa-0"
                    />
                  </v-col>

                  <!-- Switch para filtrar órdenes -->
                  <v-col cols="12" sm="3" class="d-flex align-center">
                    <v-icon
                      small
                      :color="
                        filter_type.includes('order')
                          ? notificationsColor.orderproduct || 'blue'
                          : 'grey'
                      "
                      class="mr-2"
                    >
                      fa-tag
                    </v-icon>
                    <v-switch
                      v-model="filter_type"
                      :label="$tc('order', true)"
                      :color="notificationsColor.orderproduct"
                      value="order"
                      @change="filter"
                      hide-details
                      class="ma-0 pa-0"
                    />
                  </v-col>
                </v-row>

                <!-- Componente del calendario -->
                <v-calendar
                  ref="calendar"
                  v-model="value"
                  color="primary"
                  :type="type"
                  :events="events"
                  :event-color="getEventColor"
                  :event-ripple="false"
                  :event-more="false"
                  :event-height="type === 'month' ? undefined : 16"
                  :event-margin-bottom="2"
                  @change="getEvents"
                  @click:event="showEvent"
                  :style="calendarHeight"
                  class="mt-2"
                >
                  <template v-slot:event="{ event, timed }">
                    <div
                      class="v-event-item"
                      :style="{
                        color: getEventTextColor(event),
                        height: type === 'month' ? 'auto' : '16px',
                        minHeight: '16px',
                        lineHeight: type === 'month' ? '1.2' : '16px',
                        padding: '2px 4px',
                        fontSize: type === 'month' ? '11px' : '10px',
                        whiteSpace: type === 'month' ? 'nowrap' : 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        cursor: 'pointer'
                      }"
                    >
                      <v-icon small :color="getEventTextColor(event)">{{
                        event.icon
                      }}</v-icon>
                      {{ event.title }}
                    </div>
                    <div v-if="!timed"></div>
                  </template>
                </v-calendar>

                <!-- Menú para mostrar detalles del evento seleccionado -->
                <v-menu
                  v-model="selectedOpen"
                  :close-on-content-click="false"
                  :activator="selectedElement"
                  :position-x="positionX"
                  :position-y="positionY"
                >
                  <v-card
                    light
                    class="elevation-6 rounded-lg overflow-hidden"
                    color="white"
                    :min-width="`${cardWidth}px`"
                    :max-width="`${cardWidth}px`"
                    style="border-radius: 12px"
                  >
                    <!-- Header Toolbar -->
                    <v-toolbar
                      flat
                      height="64"
                      :style="{
                        color: getEventTextColor(selectedEvent),
                        borderBottom: '1px solid rgba(0,0,0,0.12)'
                      }"
                      :color="
                        (selectedEvent.model === 'order' ||
                          selectedEvent.model === 'orderproduct') &&
                        selectedEvent.statusColor
                          ? selectedEvent.statusColor
                          : selectedEvent.background
                      "
                      dark
                    >
                      <div
                        class="d-flex flex-column justify-center mr-auto"
                        style="
                          overflow: hidden;
                          max-width: calc(100% - 90px);
                          text-align: left;
                        "
                      >
                        <div class="d-flex align-center mb-0.5">
                          <v-icon
                            x-small
                            class="mr-1.5 opacity-80"
                            style="opacity: 0.8"
                            >{{ selectedEvent.icon || 'fa-tag' }}</v-icon
                          >
                          <span
                            class="caption text-uppercase font-weight-bold opacity-80"
                            style="opacity: 0.8; letter-spacing: 0.5px"
                          >
                            {{
                              selectedEvent.model === 'orderproduct'
                                ? $tc('suborder', 1)
                                : selectedEvent.model === 'expense'
                                ? $tc('expense', 1)
                                : selectedEvent.model === 'maintenance'
                                ? $tc('equipment_maintenance', 1)
                                : $tc('order', 1)
                            }}
                          </span>
                        </div>
                        <v-toolbar-title
                          class="subtitle-1 font-weight-bold text-truncate"
                          style="line-height: 1.2"
                        >
                          {{ selectedEvent.code || selectedEvent.title }}
                        </v-toolbar-title>
                      </div>

                      <v-spacer></v-spacer>

                      <!-- Botón para abrir el formulario correspondiente -->
                      <v-btn
                        v-if="selectedEvent.model !== 'order'"
                        icon
                        small
                        class="mx-1"
                        title="Editar"
                        @click="changeRoute(selectedEvent)"
                      >
                        <v-icon size="16">fa-edit</v-icon>
                      </v-btn>

                      <!-- Botón para cerrar el menú -->
                      <v-btn
                        icon
                        small
                        class="mx-1"
                        title="Cerrar"
                        @click="selectedOpen = false"
                      >
                        <v-icon size="16">fa-times</v-icon>
                      </v-btn>
                    </v-toolbar>

                    <!-- Body for Orders / Suborders -->
                    <div
                      v-if="
                        selectedEvent.model === 'order' ||
                        selectedEvent.model === 'orderproduct'
                      "
                    >
                      <!-- Suborder product details block -->
                      <div
                        class="px-4 py-2 grey lighten-5 d-flex flex-column"
                        style="
                          border-bottom: 1px solid #e0e0e0;
                          text-align: left;
                        "
                      >
                        <span
                          class="caption text-uppercase font-weight-bold grey--text text--darken-1"
                          style="font-size: 0.75rem; letter-spacing: 0.5px"
                          >{{ $tc('product', 1) }}</span
                        >
                        <span
                          class="body-2 font-weight-bold black--text text-truncate"
                          >{{ selectedEvent.details }}</span
                        >
                      </div>

                      <!-- Suborders gauge indicators list -->
                      <div class="px-4 py-3">
                        <span
                          v-if="isAllStatusNull"
                          class="body-2 grey--text d-block my-2 text-center"
                        >
                          {{ $t('empty_states') }}
                        </span>
                        <div v-else class="my-1">
                          <suborders-list
                            ref="subordersList"
                            :order="selectedEvent.order"
                            :subOrder.sync="selectedEvent.subOrder"
                            :calendar="true"
                            :miniSubOrder.sync="subOrder"
                            @done="getEvents"
                          />
                        </div>
                      </div>
                    </div>

                    <!-- Body for other models (Expense, Maintenance) -->
                    <div
                      v-if="
                        selectedEvent.model !== 'order' &&
                        selectedEvent.model !== 'orderproduct'
                      "
                      class="px-4 py-3 black--text"
                      style="
                        min-height: 150px;
                        text-align: left;
                        background-color: #ffffff;
                      "
                    >
                      <v-row dense>
                        <!-- Cuenta -->
                        <v-col cols="12" sm="6">
                          <v-text-field
                            light
                            :label="$tc('account', 1)"
                            :value="getEventAccount(selectedEvent)"
                            disabled
                            outlined
                            dense
                            hide-details
                            class="mb-2"
                          />
                        </v-col>

                        <!-- Categoría -->
                        <v-col cols="12" sm="6">
                          <v-text-field
                            light
                            :label="$tc('category', 1)"
                            :value="getEventCategory(selectedEvent)"
                            disabled
                            outlined
                            dense
                            hide-details
                            class="mb-2"
                          />
                        </v-col>

                        <!-- Descripción -->
                        <v-col cols="12">
                          <v-textarea
                            light
                            :label="$tc('description', 1)"
                            :value="getEventDescription(selectedEvent)"
                            disabled
                            outlined
                            dense
                            rows="2"
                            hide-details
                            class="mb-2"
                          />
                        </v-col>

                        <!-- Saldo a pagar (deshabilitado) -->
                        <v-col cols="12">
                          <v-text-field
                            light
                            :label="$t('payment_amount')"
                            :value="getEventBalance(selectedEvent)"
                            disabled
                            outlined
                            dense
                            hide-details
                            class="mb-2"
                          />
                        </v-col>
                      </v-row>
                    </div>
                  </v-card>
                </v-menu>
              </v-sheet>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
      <v-col v-if="viewFilters" class="order-0 order-sm-1" cols="12" sm="3">
        <div class="filters-container">
          <div class="filters-title">
            <h3>{{ $t('filter') }}</h3>
            <div>
              <i-btn
                color="error"
                icon="fa-times"
                text
                :title="$t('close')"
                @click="viewFilters = false"
              />
            </div>
          </div>
          <!-- Activar todos los filtros -->
          <v-switch
            class="v-switch"
            color="secondary"
            :label="$tc('all', false)"
            v-model="allFilter"
          />

          <!-- Filtro: Por departamentos de producción -->
          <v-autocomplete
            v-if="allFilter"
            autocomplete="off"
            class="secondary--text"
            multiple
            outlined
            :append-icon="department_list.length > 10 ? 'fa-search' : ''"
            :clearable="department_list.length > 10"
            :items="department_list"
            item-text="name"
            item-value="pk"
            :label="$tc('department', 1)"
            @change="getEvents"
            v-model="department"
          />

          <!-- Filtro: Por plantillas de proceso de producción -->
          <v-autocomplete
            autocomplete="off"
            class="secondary--text"
            multiple
            outlined
            :append-icon="templates.length > 10 ? 'fa-search' : ''"
            :clearable="templates.length > 10"
            :items="templates"
            item-text="name"
            item-value="pk"
            :label="$t('template')"
            @change="getStatus(template)"
            v-model="template"
          />

          <!-- Filtro: Por estados de suborden -->
          <v-autocomplete
            v-if="template.length > 0"
            autocomplete="off"
            class="secondary--text"
            multiple
            outlined
            :append-icon="status_list.length > 10 ? 'fa-search' : ''"
            :clearable="status_list.length > 10"
            :items="status_list"
            item-text="name"
            item-value="pk"
            :label="$tc('status', 1)"
            @change="getEvents"
            v-model="status"
          />

          <!-- Filtro: Por etiquetas -->
          <div class="mb-4">
            <h5>{{ $tc('label', 2) }}</h5>

            <!-- Chip para mostrar todas las etiquetas -->
            <v-chip
              class="ml-1"
              color="secondary"
              text-color="white"
              @click="getEvents"
            >
              {{ $tc('all') }}
            </v-chip>

            <!-- Chips para mostrar etiquetas individuales -->
            <v-chip
              v-for="label in labels"
              :key="'chips-' + label.pk"
              class="ml-1"
              color="secondary"
              text-color="white"
              :close="label.canDelete"
              @click="filterLabel(label.name)"
              @click:close="removeChip(label)"
            >
              {{ label.name }}
            </v-chip>
          </div>

          <!-- Filtro: Por tipos de fecha -->
          <v-autocomplete
            v-if="template.length > 0 || allFilter"
            autocomplete="off"
            class="secondary--text"
            multiple
            outlined
            :append-icon="typeDate_list.length > 10 ? 'fa-search' : ''"
            :clearable="typeDate_list.length > 10"
            :items="typeDate_list"
            item-text="name"
            item-value="pk"
            :label="$tc('date', 1)"
            @change="getEvents"
            v-model="typeDate"
          />
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import SubordersList from '@/components/sale/MiniSuborders.vue'
import tinycolor from 'tinycolor2'

export default {
  components: {
    SubordersList
  },
  data() {
    return {
      value: '',
      currentRangeStart: null,
      currentRangeEnd: null,
      loading: false,
      allFilter: true,
      template: '',
      typeDate: '',
      department: '',
      status: '',
      templates: [],
      typeDate_list: [],
      status_list: [],
      department_list: [],
      subOrder: {},
      events: [],
      res_events: [],
      filter_type: ['maintenance', 'expense', 'order', 'orderproduct'],
      type: 'month',
      types: [
        { label: this.$tc('month', 1), value: 'month' },
        { label: this.$tc('week', 1), value: 'week' },
        { label: `4 ${this.$tc('days', 1)}`, value: '4day' }
      ],
      params: {
        date__gte: this.$moment().startOf('month').format('YYYY-MM-DD'),
        date__lte: this.$moment().endOf('month').format('YYYY-MM-DD')
      },
      selectedOpen: false,
      viewFilters: false,
      selectedElement: null,
      selectedEvent: {},
      currentMonth: this.$moment().format('MMMM'),
      labels: [],
      notificationsColor: {}
    }
  },
  computed: {
    ...mapGetters({
      me: 'session/me',
      company: 'company/getCompanyData'
    }),
    positionX() {
      return window.screen.width / 2 - this.cardWidth / 2
    },
    positionY() {
      return window.screen.height / 2 - this.cardHeight / 2
    },
    cardWidth() {
      return 400
    },
    cardHeight() {
      return 420
    },
    isAllStatusNull() {
      return [
        this.selectedEvent.status,
        this.selectedEvent.statusName,
        this.selectedEvent.statusDark,
        this.selectedEvent.statusColor
      ].every((value) => value === null)
    },
    calendarHeight() {
      if (this.type === 'month') {
        return { minHeight: '800px', height: 'auto !important' }
      }
      return { height: '800px !important' }
    }
  },
  methods: {
    ...mapActions({
      showConfirmation: 'confirmation/confirmationValue'
    }),

    /**
     * openFilters
     * Método para abrir los filtros de vista.
     *
     * Rosana Mendez <rosanamendez5 at gmail.com>
     */
    openFilters() {
      this.viewFilters = true
    },
    /**
     * showEvent
     * Método para mostrar la tarjeta flotante con los detalles del evento seleccionado al hacer clic.
     *
     * @param {Object} param0 - Contiene el evento nativo del navegador y el evento del calendario.
     */
    showEvent({ nativeEvent, event }) {
      const open = () => {
        this.selectedEvent = event
        this.selectedElement = nativeEvent.target
        requestAnimationFrame(() =>
          requestAnimationFrame(() => (this.selectedOpen = true))
        )
      }

      if (this.selectedOpen) {
        this.selectedOpen = false
        requestAnimationFrame(() => requestAnimationFrame(() => open()))
      } else {
        open()
      }

      if (nativeEvent && nativeEvent.stopPropagation) {
        nativeEvent.stopPropagation()
      }
    },
    /**
     * getEventColor
     * Método que obtiene el color de un evento.
     *
     * @param {Object} event - Evento del cual se obtiene el color.
     * @returns {string} - Color del evento.
     *
     * Miguel E. Villamizar R. <mevr02 at gmail.com>
     * Rosana Mendez <rosanamendez5 at gmail.com>
     */
    getEventColor(event) {
      const color = event.statusColor || event.background || event.color
      return color || '#1976D2' // Azul por defecto si no hay color
    },
    /**
     * getEventTextColor
     * Método que obtiene el color de texto de un evento en base al fondo.
     *
     * @param {Object} event - Evento del cual se evalúa.
     * @returns {string} - Color del texto del evento (blanco o negro).
     */
    getEventTextColor(event) {
      const color = this.getEventColor(event)
      if (color) {
        const cleanColor = color.toLowerCase().trim()
        if (
          cleanColor === '#28a745' ||
          cleanColor === '#4caf50' ||
          cleanColor === '#ff5722'
        ) {
          return 'black'
        }
      }
      return tinycolor(color).isDark() ? 'white' : 'black'
    },
    /**
     * getEvents
     * Método que consulta y filtra los eventos de gastos mantenimiento de
     * equipo y subOrdenes en el calendario donde los filtros aplicados son:
     * Rango de fechas
     * Para las subOrdenes: departamento de producción, plantilla del producto,
     * estado y tipos de fecha de la subOrden.
     *
     * @param {Object} params - Parámetros que contienen rango de fechas.
     *
     * Miguel E. Villamizar R. <mevr02 at gmail.com>
     * Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getEvents({ start, end } = {}) {
      if (start) this.currentRangeStart = start
      if (end) this.currentRangeEnd = end

      const params = {
        date__gte: start
          ? start.date
          : this.currentRangeStart
          ? this.currentRangeStart.date
          : this.$moment().startOf('month').format('YYYY-MM-DD'),
        date__lte: end
          ? end.date
          : this.currentRangeEnd
          ? this.currentRangeEnd.date
          : this.$moment().endOf('month').format('YYYY-MM-DD')
      }

      if (start?.date) {
        this.currentMonth = this.$moment(start.date).format('MMMM')
      }

      const filters = {
        department:
          this.department?.length > 0 ? this.department.toString() : null,
        template: this.template?.length > 0 ? this.template.toString() : null,
        status: this.status?.length > 0 ? this.status.toString() : null,
        typeDate: this.typeDate?.length > 0 ? this.typeDate.toString() : null
      }

      try {
        this.loading = true
        const response = await this.$api.notifications.list({
          opt: {
            params: {
              ...params,
              ...filters
            }
          }
        })
        this.returnEvents(end, start, response)
      } catch (error) {
        console.error('Error fetching events:', error)
      } finally {
        this.loading = false
      }
    },
    /**
     * returnEvents
     * Método que procesa y devuelve los eventos filtrados.
     *
     * @param {Object} end - Fin del rango de fechas.
     * @param {Object} start - Inicio del rango de fechas.
     * @param {Object} response - Respuesta de la API con los eventos.
     * @param {boolean} timed - Indica si el evento es cronometrado.
     *
     * Miguel E. Villamizar R. <mevr02 at gmail.com>
     * Rosana Mendez <rosanamendez5 at gmail.com>
     */
    returnEvents(end, start, response, timed) {
      const processedEvents = response.data
        .filter((notification) => notification.timestamp !== null)
        .map((notification, index) => {
          let eventDate = new Date(notification.timestamp)
          let userTimezoneOffset = eventDate.getTimezoneOffset() * 60000
          let adjustedDate = new Date(eventDate.getTime() + userTimezoneOffset)
          let startDate = new Date(adjustedDate)
          let endDate = new Date(adjustedDate.getTime() + 1)

          let eventModel = 'notification'
          let eventIcon = 'fa-comments'
          let eventCode = ''
          let eventDetails = ''
          let eventTitle = ''
          if (notification.type === 1) {
            eventModel = 'maintenance'
            eventIcon = 'fa-wrench'
          } else if (notification.type === 2) {
            eventModel = 'expense'
            eventIcon = 'fa-money-bill'
          } else if (notification.type === 5) {
            eventModel = 'orderproduct'
            eventIcon = 'fa-tag'
          }

          // Parsear mensaje si es JSON
          let parsedData = null
          if (
            notification.message &&
            typeof notification.message === 'string' &&
            notification.message.trim().startsWith('{')
          ) {
            try {
              parsedData = JSON.parse(notification.message)
            } catch (e) {
              console.warn('Error parseando JSON:', e.message)
            }
          }

          let eventOrder = null
          let eventSubOrder = null

          // Construir título según el modelo
          if (eventModel === 'order' || eventModel === 'orderproduct') {
            if (notification.suborder_info) {
              const info = notification.suborder_info
              eventOrder = info.order_id
              eventSubOrder = notification.stored_id

              eventTitle =
                parsedData && parsedData.kwargs && parsedData.kwargs.name
                  ? parsedData.kwargs.name
                  : parsedData && parsedData.kwargs && parsedData.kwargs.code
                  ? parsedData.kwargs.code
                  : this.$tc('suborder', 1)

              eventCode =
                parsedData && parsedData.kwargs && parsedData.kwargs.code
                  ? parsedData.kwargs.code
                  : ''

              eventDetails =
                parsedData && parsedData.kwargs
                  ? `${parsedData.kwargs.name || ''} (${
                      parsedData.kwargs.status || ''
                    })`
                  : ''

              let startD = info.order_date
              let endD = info.promise_date

              // Check if typeDate filter is active and matching milestone exists
              if (this.typeDate && this.typeDate.length > 0) {
                const typeDateIds = Array.isArray(this.typeDate)
                  ? this.typeDate
                  : [this.typeDate]
                const matchingDate = info.suborder_dates.find((d) =>
                  typeDateIds.includes(d.type_id)
                )
                if (matchingDate) {
                  startD = matchingDate.date
                  endD = matchingDate.date
                }
              }

              if (startD && endD) {
                const dateStart = new Date(startD + 'T00:00:00')
                const dateEnd = new Date(endD + 'T23:59:59')
                if (dateStart <= dateEnd) {
                  startDate = dateStart
                  endDate = dateEnd
                } else {
                  startDate = dateEnd
                  endDate = dateStart
                }
              } else if (endD) {
                startDate = new Date(endD + 'T00:00:00')
                endDate = new Date(endD + 'T23:59:59')
              } else if (startD) {
                startDate = new Date(startD + 'T00:00:00')
                endDate = new Date(startD + 'T23:59:59')
              }
            } else {
              // Fallback to legacy structure
              if (parsedData && parsedData.kwargs && parsedData.kwargs.name) {
                eventTitle = parsedData.kwargs.name
              } else if (
                parsedData &&
                parsedData.kwargs &&
                parsedData.kwargs.code &&
                parsedData.kwargs.code !== ''
              ) {
                eventTitle = parsedData.kwargs.code
              } else {
                eventTitle = this.$tc('order', 1)
              }

              if (parsedData && parsedData.order) {
                eventOrder = parseInt(parsedData.order)
              }
              eventSubOrder = notification.stored_id

              if (parsedData && parsedData.kwargs && parsedData.kwargs.code) {
                eventCode = parsedData.kwargs.code
              }
              eventDetails = notification.message || ''

              if (parsedData && parsedData.kwargs) {
                const { date_created, delivery_date } = parsedData.kwargs
                if (date_created) {
                  startDate = new Date(date_created + 'T00:00:00')
                }
                if (delivery_date) {
                  endDate = new Date(delivery_date + 'T23:59:59')
                } else if (date_created) {
                  endDate = new Date(date_created + 'T23:59:59')
                }
              }
            }
          } else {
            // Para gastos, mantenimientos, etc.
            let rawMessage = notification.message || ''
            if (parsedData) {
              if (parsedData.message) rawMessage = parsedData.message
              else if (parsedData.description)
                rawMessage = parsedData.description
              else rawMessage = JSON.stringify(parsedData) // último recurso
            }
            eventTitle = rawMessage
          }

          // Si el título está vacío, poner texto por defecto
          if (!eventTitle) {
            eventTitle = this.$tc('event', 1)
          }

          // Función auxiliar para extraer texto plano de HTML
          const stripHtml = (html) => {
            if (!html || typeof html !== 'string') return html
            const doc = new DOMParser().parseFromString(html, 'text/html')
            return doc.body.textContent || ''
          }

          // Limpiar tags HTML y entidades de la descripción y del título
          eventDetails = stripHtml(eventDetails)
          eventTitle = stripHtml(eventTitle)

          // Color del evento
          let eventColor =
            notification.background || notification.color || '#1976D2'
          if (eventColor === '#ffffff' || !eventColor) {
            const colors = {
              maintenance: '#FF5722',
              expense: '#4CAF50',
              orderproduct: '#9C27B0',
              notification: '#607D8B'
            }
            eventColor = colors[eventModel] || '#1976D2'
          }
          return {
            title: eventTitle,
            name: eventTitle,
            start: startDate,
            end: endDate,
            color: eventColor,
            timed: false,
            pk: notification.pk,
            timestamp: notification.timestamp,
            type: notification.type,
            model: eventModel,
            details: eventDetails,
            code: eventCode,
            statusColor: eventColor,
            background: eventColor,
            icon: eventIcon,
            status: notification.status,
            statusName: notification.statusName,
            statusDark: notification.statusDark,
            paid: notification.paid,
            label: notification.label,
            labelName: notification.labelName,
            expense: notification.expense,
            maintenance: notification.maintenance,
            expense_info: notification.expense_info || null,
            maintenance_info: notification.maintenance_info || null,
            order: eventOrder,
            subOrder: eventSubOrder,
            stored_id: notification.stored_id,
            suborder_info: notification.suborder_info,
            suborderLabels:
              notification.suborder_info && notification.suborder_info.labels
                ? notification.suborder_info.labels.map((l) => l.nameLabel)
                : [],
            groupKey: `${eventDate.toDateString()}-${eventTitle}-${eventModel}-${
              notification.pk
            }`
          }
        })

      const filteredEvents = processedEvents.filter(
        (event) => event.model !== 'notification'
      )
      this.events = filteredEvents
      this.res_events = [...this.events]
    },
    /**
     * getTemplate
     * Método que obtiene las plantillas de productos.
     *
     * Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getTemplate() {
      let template = await this.$api.product.template.list({
        opt: {
          params: {
            order: true,
            company: this.company.pk
          }
        }
      })
      this.templates = template.data
    },
    /**
     * getStatus
     * Método que obtiene el estado de las plantillas seleccionadas.
     *
     * @param {Array} selectedTemplates - Plantillas seleccionadas.
     *
     * Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getStatus(selectedTemplates) {
      let status_list
      if (
        (selectedTemplates && selectedTemplates.length === 0) ||
        this.allFilter
      ) {
        let status = await this.$api.sale.status.list({})
        status_list = status.data
      } else {
        let status = await this.$api.sale.status.show({
          pk: selectedTemplates.join(','),
          opt: {
            params: {
              template: true
            }
          }
        })
        status_list = status.data
      }
      this.status_list = status_list.filter((item) =>
        selectedTemplates.includes(item.template)
      )
      this.getTypeDate()
      this.getEvents(this.params)
    },
    /**
     * getTypeDate
     * Método que obtiene los tipos de fecha según la plantilla.
     *
     * Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getTypeDate() {
      if (this.template.length === 0 || this.allFilter) {
        let typeDate = await this.$api.dashboard.typedate.list({
          opt: {
            params: {
              company: this.company?.pk
            }
          }
        })
        this.typeDate_list = typeDate.data.results.filter(
          (event) => event.name !== 'promise'
        )
      } else {
        let typeDate = await this.$api.dashboard.typedate.show({
          pk: this.template,
          opt: {
            params: {
              template: true,
              company: this.company?.pk
            }
          }
        })
        this.typeDate_list = typeDate.data.filter(
          (event) => event.name !== 'promise'
        )
      }
    },
    getEventAccount(event) {
      if (event.model === 'expense') {
        return event.expense_info?.account_name || ''
      } else if (event.model === 'maintenance') {
        return event.maintenance_info?.account_name || ''
      }
      return ''
    },
    getEventCategory(event) {
      if (event.model === 'expense') {
        return event.expense_info?.category_name || ''
      } else if (event.model === 'maintenance') {
        return event.maintenance_info?.category_name || 'Mantenimiento'
      }
      return ''
    },
    getEventDescription(event) {
      if (event.model === 'expense') {
        return (
          event.expense_info?.description || event.details || event.title || ''
        )
      } else if (event.model === 'maintenance') {
        return (
          event.maintenance_info?.description ||
          event.details ||
          event.title ||
          ''
        )
      }
      return event.details || event.title || ''
    },
    getEventBalance(event) {
      let balance = 0
      if (event.model === 'expense') {
        balance = event.expense_info?.balance ?? 0
      } else if (event.model === 'maintenance') {
        balance = event.maintenance_info?.balance ?? 0
      }
      return `$ ${parseFloat(balance).toLocaleString('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`
    },
    /**
     * getDepartment
     * Método que obtiene la lista de departamentos.
     *
     * Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getDepartment() {
      let department = await this.$api.department.list({})
      this.department_list = department.data.results
    },

    /**
     * filter
     * Método que filtra los eventos según el tipo.
     *
     * Rosana Mendez <rosanamendez5 at gmail.com>
     */
    filter() {
      const filter_type = this.filter_type.includes('order')
        ? this.filter_type
        : this.filter_type.filter((event) => event !== 'orderproduct')

      this.events = this.res_events.filter((event) =>
        filter_type.includes(event.model)
      )
    },
    /**
     * getLabels
     * Método que obtiene las etiquetas personalizadas de subórdenes.
     */
    async getLabels() {
      try {
        let response = await this.$api.suborder.labels.list({})
        let fetchedLabels = response.data.results || response.data || []
        let uniqueLabelsMap = {}
        fetchedLabels.forEach((label) => {
          const name = label.nameLabel || label.name
          if (name && !uniqueLabelsMap[name]) {
            uniqueLabelsMap[name] = {
              pk: label.pk,
              name: name,
              canDelete: label.canDelete
            }
          }
        })
        this.labels = Object.values(uniqueLabelsMap)
      } catch (error) {
        console.error('Error al obtener etiquetas:', error)
      }
    },
    /**
     * filterLabel
     * Método que filtra los eventos según la etiqueta seleccionada.
     *
     * @param {string} labelName - Nombre de la etiqueta seleccionada.
     */
    filterLabel(labelName) {
      if (!labelName) {
        this.events = [...this.res_events]
        return
      }
      this.events = this.res_events.filter((event) => {
        if (event.suborderLabels && Array.isArray(event.suborderLabels)) {
          return event.suborderLabels.includes(labelName)
        }
        return false
      })
    },
    /**
     * changeRoute
     * Método que cambia la ruta según el modelo del evento.
     *
     * @param {Object} item - Evento cuyo modelo se usará para la ruta.
     *
     * Miguel E. Villamizar R. <mevr02 at gmail.com>
     * Rosana Mendez <rosanamendez5 at gmail.com>
     * Daniel Contreras <exodiadaniel at gmail.com>
     */
    refreshEvents() {
      this.getEvents(this.params)
    },
    changeRoute(item) {
      console.log('Calendar changeRoute selectedEvent item:', item)
      if (item.model === 'expense') {
        const dateStr = this.$moment.utc(item.timestamp).format('YYYY-MM-DD')
        this.$router.push({
          name: 'expensesRegular-pk',
          params: { pk: this.codification({ code: 'create' }) },
          query: { base_id: item.stored_id, event_id: item.pk, date: dateStr }
        })
      } else if (item.model === 'maintenance') {
        const dateStr = this.$moment.utc(item.timestamp).format('YYYY-MM-DD')
        this.$router.push({
          name: 'maintenance-pk',
          params: { pk: this.codification({ code: item.stored_id }) },
          query: { event_id: item.pk, date: dateStr }
        })
      } else if (item.model === 'orderproduct' || item.model === 'order') {
        if (this.$refs.subordersList) {
          this.$refs.subordersList.openSuborderModal(item.stored_id)
        }
      }
    },
    /**
     * removeChip
     * Método que muestra una confirmación antes de eliminar una etiqueta.
     *
     * @param {Object} item - Etiqueta a eliminar.
     *
     * Miguel E. Villamizar R. <mevr02 at gmail.com>
     * Rosana Mendez <rosanamendez5 at gmail.com>
     */
    removeChip(item) {
      this.showConfirmation({
        show: true,
        model: this.$tc('label', 1),
        title: this.$tc('delete', 1),
        type: 'error white--text',
        content: this.$t('confirm_delete'),
        alert: `${this.$t('success', {
          model: this.$tc('label', 1),
          action: this.$tc('delete', 2)
        })}`,
        confirm: () => this.$api.suborder.labels.remove({ pk: item.pk })
      })
    },
    /**
     * getNotifications
     * Método que consulta y filtra los tipos de notificación:
     * gastos, mantenimiento de equipo y subOrdenes de venta para obtener el
     * color configurado en notificaciones y aplicarlo a los swicth del
     * calendario.
     *
     * Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getNotifications() {
      try {
        const notification = await this.$api.notifications.list({})
        this.notifications = notification.data
        this.notificationsColor = {}

        const contentTypes = ['expense', 'maintenance', 'orderproduct']
        contentTypes.forEach((type) => {
          const notificationItem = this.notifications.find(
            (m) => m.content_type && m.content_type.model === type
          )
          if (notificationItem) {
            this.notificationsColor[type] = notificationItem.background
          }
        })
      } catch (error) {
        console.error('Error al obtener notificaciones:', error)
      }
    }
  },
  created() {
    this.unwatch = this.$store.watch(
      () => this.$store.getters['confirmation/getConfirmationFinished'],
      async (val) => {
        if (val === this.$tc('label', 1)) {
          this.getLabels()
          this.deleting = false
          this.$store.dispatch('confirmation/confirmationFinished', false)
        }
      }
    )
    this.$root.$on('payment-processed', this.refreshEvents)
  },
  beforeDestroy() {
    this.unwatch()
    this.$root.$off('payment-processed', this.refreshEvents)
  },
  mounted() {
    this.getEvents(this.params)
    this.getTemplate()
    this.getTypeDate()
    this.getDepartment()
    this.getNotifications()
    this.getLabels()
  }
}
</script>
<style scoped lang="sass">
.calendar-container
  min-height: 800px

.my-event
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap
  border-radius: 2px
  background-color: #1867c0
  color: #ffffff
  border: 1px solid #1867c0
  width: 100%
  font-size: 12px
  padding: 3px
  cursor: pointer
  margin-bottom: 1px

::v-deep .v-calendar-monthly
  .v-calendar-weekly__week
    min-height: 120px
    height: auto !important

  .v-calendar-weekly__day
    min-height: 120px
    height: auto !important

  .v-event
    height: auto !important
    min-height: 20px
    white-space: normal
    overflow: visible

::v-deep
  & .v-calendar-daily, & .v-calendar-weekly
    .v-calendar-weekly__week
      height: auto

.v-event-item
  padding-left: 6px

.filters-container
  border: 1px solid rgba(132, 132, 132, 0.29)
  padding: 12px
  border-radius: 12px

.filters-title
  display: flex
  justify-content: space-between
  border-bottom: 1px solid rgba(132, 132, 132, 0.29)

::v-deep .v-card.v-card--light
  .v-input--is-disabled input, .v-input--is-disabled textarea
    color: rgba(0, 0, 0, 0.87) !important
  .v-input--is-disabled .v-label
    color: rgba(0, 0, 0, 0.6) !important
</style>
